import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useNotification } from "../../../contexts/NotificationContext";
import { isAdminEmail } from "../../../constants/admin";
import { Eye, EyeOff, Mail, ArrowRight } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, googleSignIn } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      showSuccess("Logged in successfully!");
      navigate(isAdminEmail(email) ? "/admin" : "/");
    } catch (error) {
      console.error("Login error:", error);
      showError("Login failed. Please check your credentials.");
    }
  };

  const handleGoogle = async () => {
    try {
      const user = await googleSignIn();
      showSuccess("Signed in with Google successfully!");
      navigate(isAdminEmail(user?.email) ? "/admin" : "/");
    } catch (error) {
      console.error("Google sign-in error:", error);
      showError("Google sign-in failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#3c3530] lg:flex">
      {/* Form Side */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-10 sm:py-14">
        <Link to="/" className="mb-8 sm:mb-10 self-start">
          <img src="/Logo.png" alt="Glowy Logo" className="h-12 sm:h-14 object-contain" />
        </Link>

        <div className="max-w-md w-full mx-auto lg:mx-0">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#E3D5C5] mb-4 sm:mb-6 leading-tight">
            Welcome Back!
          </h2>
          <p className="text-white/80 text-sm sm:text-base mb-8 sm:mb-10">
            Access your account to track orders, view your wishlist, and enjoy exclusive
            offers. Don't have an account yet? Sign up today for a seamless shopping
            experience!
          </p>

          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 w-5 h-5" />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                className="pl-12 pr-4 h-12 w-full rounded-full border-0 text-gray-800 placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#DDBB92]"
                style={{ backgroundColor: "#f0e7dc" }}
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="off"
                className="pl-4 pr-12 h-12 w-full rounded-full border-0 text-gray-800 placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#DDBB92]"
                style={{ backgroundColor: "#f0e7dc" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600"
                aria-label="Toggle password"
              >
                {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>

            <button
              type="submit"
              className="w-full h-12 inline-flex items-center justify-center rounded-full font-medium text-gray-900 hover:opacity-90 transition"
              style={{ backgroundColor: "#e0c4a3" }}
            >
              Login <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </form>

          <div className="text-center text-white/70 text-xs sm:text-sm my-5 sm:my-6">-OR-</div>

          <div className="flex justify-center gap-4 mb-6 sm:mb-8">
            <button
              onClick={handleGoogle}
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:bg-gray-50 transition"
              aria-label="Sign in with Google"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>
            <button
              className="w-12 h-12 rounded-full flex items-center justify-center hover:opacity-90 transition"
              style={{ backgroundColor: "#1877f2" }}
              aria-label="Sign in with Facebook"
            >
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
          </div>

          <div className="text-center">
            <span className="text-white/70 text-sm">Don't have an account yet? </span>
            <Link to="/signup" className="text-white font-medium text-sm hover:underline">
              Sign up →
            </Link>
          </div>
        </div>
      </div>

      {/* Image Side (desktop only) */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 xl:p-14">
        <div className="bg-[#e0c4a3] rounded-3xl p-6 shadow-lg w-full max-w-lg">
          <div className="relative flex flex-col items-center">
            <img
              src="/LoginPic.png"
              alt="Skincare"
              className="object-contain w-full rounded-xl mb-8 max-h-[540px]"
            />
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-6 bg-[#fce9f5] rounded-xl p-4 shadow-md w-[90%]">
              <p className="text-[#463C30] text-base xl:text-xl italic font-light leading-relaxed text-center font-serif">
                "Beautiful skin starts with self-care—nourish it, and let your inner glow shine through"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
